import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react'
import { Plus, ChevronDown, ChevronUp, Circle, FileText, Link, GripVertical } from 'lucide-react'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import DataGrid from '@/components/data/DataGrid'
import Button from '@/components/ui/Button'
import Tag from '@/components/ui/Tag'
import { designTokens } from '@/utils/design-tokens'
import type { TaskLabelInfo } from '@/api/generated/models/TaskLabelInfo'
import type { TaskDetailInfo } from '@/api/generated/models/TaskDetailInfo'
import type { TaskExtraInfo } from '@/api/generated/models/TaskExtraInfo'
import type { OrgMemberResponse } from '@/api/generated/models/OrgMemberResponse'
import { TaskGroupService } from '@/api/generated/services/TaskGroupService'
import { TaskSubgroupService } from '@/api/generated/services/TaskSubgroupService'
import { TaskMapService } from '@/api/generated/services/TaskMapService'
import { TaskItemService } from '@/api/generated/services/TaskItemService'
import { useAlert } from '@/components/ui/Alert/exports'

/**
 * Type definitions for the hierarchical task data
 * 
 * API Endpoint: GET /task/workspace-all (appsTaskViewsListAllTaskInWorkspace)
 * 
 * The assignee, sub_assignee, manager, and reviewer objects should include:
 * - id: existing user identifier
 * - name: user display name
 * - email: user email address
 * - account_id: user's account ID for authentication/authorization
 */
export interface TaskUser {
  id: string
  name: string
  email: string
  phone: string
  /**
   * 계정 ID - Used for authentication and user identification
   */
  account_id: string
}

export interface TaskItem {
  id: string
  type: 'task_item'
  name: string
  start_at: string
  end_at: string
  status: string
  output: string
  department_id: string
  department_name: string
  assignee: TaskUser
  sub_assignee: TaskUser
  manager: TaskUser
  reviewer: TaskUser
}

export interface BudgetItem {
  id: string
  name: string
  target_figure: string
  done_figure: string
}

export interface Budget {
  id: string
  result_description: string
  items: BudgetItem[]
}

export interface PerformanceItem {
  id: string
  name: string
  target_figure: string
  done_figure: string
}

export interface Performance {
  id: string
  status: string
  name: string
  unit: string
  items: PerformanceItem[]
}

export interface Task {
  id: string
  type: 'task'
  task_subgroup_id: string
  name: string
  origin_name: string
  department_id: string
  department_name: string
  assignee: TaskUser
  sub_assignee: TaskUser
  manager: TaskUser
  reviewer: TaskUser
  budget: Budget
  performance: Performance
  task_items: TaskItem[]
  status: string
  start_at: string
  end_at: string
  task_group_name: string
  task_subgroup_name: string
  business_name: string
  business_id: string
  labels?: Array<TaskLabelInfo>
  detail_infos?: Array<TaskDetailInfo>
  extra_infos?: Array<TaskExtraInfo>
  /**
   * 원본 여부 - true: 원본 세부실행과제, false: 연결된 세부실행과제
   */
  is_origin?: boolean
  /**
   * 연결 여부 - true: 다른 실행과제에서 연결됨
   */
  is_linked?: boolean
  /**
   * 성과 연결 여부 - true: 성과 지표가 연결됨
   */
  is_performance_linked?: boolean
  /**
   * TaskMap ID - 연결된 세부실행과제 삭제 시 사용
   */
  task_map_id?: string
  /**
   * 재정의 과제명 - 연결 세부실행과제에서 설정한 이름
   */
  task_map_overwrite_name?: string | null
}

export interface TaskSubgroup {
  id: string
  type: 'task_subgroup'
  task_group_id: string
  name: string
  tasks: Task[]
}

export interface TaskGroup {
  id: string
  type: 'task_group'
  business_id: string
  business_name: string
  workspace_id: string
  workspace_name: string
  name: string
  task_subgroups: TaskSubgroup[]
}

export interface Business {
  id: string
  type: 'business'
  name: string
  sort_key: string
  workspace_id: string
  workspace_name: string
  task_groups: TaskGroup[]
}

// Flattened row type for AG-Grid tree data
export interface TaskTreeNode {
  id: string
  type: 'business' | 'task_group' | 'task_subgroup' | 'task' | 'task_item'
  name: string
  origin_name?: string
  status?: string
  business_name?: string
  department_name?: string
  assignee?: TaskUser
  sub_assignee?: TaskUser
  manager?: TaskUser
  reviewer?: TaskUser
  orgHierarchy: string[]  // Required for AG-Grid tree data
  originalData: Business | TaskGroup | TaskSubgroup | Task | TaskItem
}

interface TaskHierarchyTableProps {
  data: Business[]
  isExpanded?: boolean
  isLoading?: boolean
  onRowClick?: (task: TaskTreeNode) => void
  onCreateTaskGroup?: (businessId: string) => void
  onCreateTaskSubgroup?: (taskGroupId: string) => void
  onCreateTask?: (taskSubgroupId: string) => void
  onCreateTaskItem?: (taskId: string) => void
  onSetActionButtonClicked?: (clicked: boolean) => void
  onReportClick?: (taskItem: TaskTreeNode) => void
  orgMember?: OrgMemberResponse | null
  /** 순서 변경을 위한 데이터 업데이트 콜백 */
  onDataUpdate?: (newData: Business[]) => void
  /** 외부에서 관리하는 접힌 항목 Set (상태 유지용) */
  collapsedItems?: Set<string>
  /** 접힌 항목 변경 콜백 */
  onCollapsedItemsChange?: (collapsedItems: Set<string>) => void
  /** 현재 선택된 항목 ID (상세 패널에 표시 중인 항목) */
  selectedTaskId?: string
  /** 스크롤할 항목 ID (설정되면 해당 행으로 스크롤) */
  scrollToTaskId?: string | null
  /** 스크롤 완료 후 호출되는 콜백 */
  onScrollComplete?: () => void
  /** 새로 생성된 항목 ID (하이라이트 표시용) */
  newlyCreatedTaskId?: string | null
  /** 표에 보일 유형. 없으면 모든 유형을 보인다 */
  visibleTypes?: TaskTreeNode['type'][]
  /** 숨길 컬럼 field 목록 */
  hiddenColumns?: string[]
}

// Drag State 타입 정의
interface DragState {
  isDragging: boolean
  draggedItem: TaskTreeNode | null
  draggedItemType: string | null
  draggedItemParentId: string | null
  dropTargetId: string | null
  dropPosition: 'before' | 'after' | null
}

const TaskHierarchyTable: React.FC<TaskHierarchyTableProps> = ({
  data = [],
  isExpanded = true,
  isLoading = false,
  onRowClick,
  onCreateTaskGroup,
  onCreateTaskSubgroup,
  onCreateTask,
  onCreateTaskItem,
  onSetActionButtonClicked,
  onReportClick,
  orgMember,
  onDataUpdate,
  collapsedItems: externalCollapsedItems,
  onCollapsedItemsChange,
  selectedTaskId,
  scrollToTaskId,
  onScrollComplete,
  newlyCreatedTaskId,
  visibleTypes,
  hiddenColumns
}) => {
  const { showSuccess, showError } = useAlert()

  // Drag & Drop State
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedItem: null,
    draggedItemType: null,
    draggedItemParentId: null,
    dropTargetId: null,
    dropPosition: null
  })

  // Ref for drag data (데이터 손실 방지)
  const dragDataRef = useRef<TaskTreeNode | null>(null)

  // Ref for auto-scroll interval
  const scrollIntervalRef = useRef<number | null>(null)

  // Ref for grid container (스크롤 이벤트용)
  const gridContainerRef = useRef<HTMLDivElement | null>(null)

  // Ref for AG-Grid API
  const gridApiRef = useRef<any>(null)

  // 이전 selectedTaskId 추적
  const prevSelectedTaskIdRef = useRef<string | undefined>(undefined)

  // selectedTaskId 변경 시 해당 행만 다시 그리기 (깜빡임 방지)
  useEffect(() => {
    if (gridApiRef.current) {
      const api = gridApiRef.current
      const rowNodesToRedraw: any[] = []

      // 이전에 선택된 행 찾기
      if (prevSelectedTaskIdRef.current) {
        const prevNode = api.getRowNode(prevSelectedTaskIdRef.current)
        if (prevNode) {
          rowNodesToRedraw.push(prevNode)
        }
      }

      // 새로 선택된 행 찾기
      if (selectedTaskId) {
        const newNode = api.getRowNode(selectedTaskId)
        if (newNode) {
          rowNodesToRedraw.push(newNode)
        }
      }

      // 변경된 행만 다시 그리기
      if (rowNodesToRedraw.length > 0) {
        setTimeout(() => {
          api.redrawRows({ rowNodes: rowNodesToRedraw })
        }, 0)
      }

      // 이전 값 업데이트
      prevSelectedTaskIdRef.current = selectedTaskId
    }
  }, [selectedTaskId])

  // newlyCreatedTaskId 변경 시 이전/새 행 redraw (하이라이트 갱신)
  const prevNewlyCreatedTaskIdRef = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    if (gridApiRef.current) {
      const api = gridApiRef.current
      const rowNodesToRedraw: any[] = []

      if (prevNewlyCreatedTaskIdRef.current) {
        const prevNode = api.getRowNode(prevNewlyCreatedTaskIdRef.current)
        if (prevNode) rowNodesToRedraw.push(prevNode)
      }

      if (newlyCreatedTaskId) {
        const newNode = api.getRowNode(newlyCreatedTaskId)
        if (newNode) rowNodesToRedraw.push(newNode)
      }

      if (rowNodesToRedraw.length > 0) {
        setTimeout(() => {
          api.redrawRows({ rowNodes: rowNodesToRedraw })
        }, 0)
      }
    }
    prevNewlyCreatedTaskIdRef.current = newlyCreatedTaskId
  }, [newlyCreatedTaskId])

  // 외부에서 collapsedItems를 관리하는 경우 외부 상태 사용, 아니면 내부 상태 사용
  const [internalCollapsedItems, setInternalCollapsedItems] = useState<Set<string>>(new Set())

  // 실제 사용할 collapsedItems (외부 또는 내부)
  const collapsedItems = externalCollapsedItems ?? internalCollapsedItems

  // collapsedItems 업데이트 함수
  const setCollapsedItems = useCallback((updater: Set<string> | ((prev: Set<string>) => Set<string>)) => {
    const newValue = typeof updater === 'function' ? updater(collapsedItems) : updater
    if (onCollapsedItemsChange) {
      onCollapsedItemsChange(newValue)
    } else {
      setInternalCollapsedItems(newValue)
    }
  }, [collapsedItems, onCollapsedItemsChange])

  // Toggle collapse/expand for an item
  const toggleCollapse = useCallback((itemId: string) => {
    setCollapsedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(itemId)) {
        newSet.delete(itemId)
      } else {
        newSet.add(itemId)
      }
      return newSet
    })
  }, [setCollapsedItems])

  // Check if an item should be hidden due to collapsed parent
  const isItemHidden = useCallback((orgHierarchy: string[], _itemId: string) => {
    // Check if any parent in the hierarchy is collapsed
    for (let i = 0; i < orgHierarchy.length - 1; i++) {
      const parentPath = orgHierarchy.slice(0, i + 1).join('|')
      if (collapsedItems.has(parentPath)) {
        return true
      }
    }
    return false
  }, [collapsedItems])

  // Check if item has children
  const hasChildren = useCallback((item: TaskTreeNode, allData: TaskTreeNode[]) => {
    return allData.some(dataItem =>
      dataItem.orgHierarchy.length === item.orgHierarchy.length + 1 &&
      dataItem.orgHierarchy.slice(0, item.orgHierarchy.length).join('|') === item.orgHierarchy.join('|')
    )
  }, [])

  // 선택된 행 클래스 (isSelected 플래그 및 새로 생성된 항목 기반)
  const getRowClass = useCallback((params: any) => {
    const classes: string[] = []
    if (params.data?.isSelected) {
      classes.push('ag-row-selected-custom')
    }
    if (newlyCreatedTaskId && params.data?.id === newlyCreatedTaskId) {
      classes.push('ag-row-newly-created')
    }
    return classes.join(' ')
  }, [newlyCreatedTaskId])

  // ===== Drag & Drop 유틸리티 함수 =====

  // 부모 ID 가져오기
  const getParentId = useCallback((item: TaskTreeNode): string | null => {
    switch (item.type) {
      case 'task_group':
        return (item.originalData as TaskGroup).business_id
      case 'task_subgroup':
        return (item.originalData as TaskSubgroup).task_group_id
      case 'task':
        return (item.originalData as Task).task_subgroup_id
      case 'task_item':
        // task_item의 경우 부모 task를 찾아야 함
        for (const business of data) {
          for (const tg of business.task_groups) {
            for (const ts of tg.task_subgroups) {
              for (const t of ts.tasks) {
                if (t.task_items.some(ti => ti.id === item.id)) {
                  return t.id
                }
              }
            }
          }
        }
        return null
      default:
        return null
    }
  }, [data])

  // 드롭 대상 유효성 검사
  const validateDropTarget = useCallback((
    draggedItem: TaskTreeNode,
    targetItem: TaskTreeNode
  ): boolean => {
    // 같은 유형인지 확인
    if (draggedItem.type !== targetItem.type) {
      return false
    }

    // 자기 자신인지 확인
    if (draggedItem.id === targetItem.id) {
      return false
    }

    // 같은 상위 범위인지 확인
    const draggedParentId = getParentId(draggedItem)
    const targetParentId = getParentId(targetItem)

    return draggedParentId === targetParentId
  }, [getParentId])

  // Transform hierarchical data to flat tree structure for AG-Grid
  const treeData = useMemo(() => {
    const flatData: TaskTreeNode[] = []

    // Process each business directly from the data array
    data.forEach((business) => {
      // Add business level
      flatData.push({
        id: business.id,
        type: 'business',
        name: business.name,
        business_name: business.name,
        orgHierarchy: [business.name],
        originalData: business
      })

      // Process task groups under this business
      business.task_groups.forEach(taskGroup => {
        const taskGroupPath = [business.name, taskGroup.name]

        // Add task group
        flatData.push({
          id: taskGroup.id,
          type: 'task_group',
          name: taskGroup.name,
          business_name: business.name,
          orgHierarchy: taskGroupPath,
          originalData: taskGroup
        })

        // Process task subgroups
        taskGroup.task_subgroups.forEach(taskSubgroup => {
          const subgroupPath = [...taskGroupPath, taskSubgroup.name]
          
          flatData.push({
            id: taskSubgroup.id,
            type: 'task_subgroup',
            name: taskSubgroup.name,
            business_name: business.name,
            orgHierarchy: subgroupPath,
            originalData: taskSubgroup
          })

          // Process tasks
          taskSubgroup.tasks.forEach(task => {
            const taskPath = [...subgroupPath, task.name]
            
            flatData.push({
              id: task.id,
              type: 'task',
              name: task.name,
              origin_name: task.origin_name,
              status: task.status,
              business_name: business.name,
              department_name: task.department_name,
              assignee: task.assignee,
              sub_assignee: task.sub_assignee,
              manager: task.manager,
              reviewer: task.reviewer,
              orgHierarchy: taskPath,
              originalData: task
            })

            // Process task items
            task.task_items.forEach(taskItem => {
              const itemPath = [...taskPath, taskItem.name]
              
              flatData.push({
                id: taskItem.id,
                type: 'task_item',
                name: taskItem.name,
                status: taskItem.status,
                business_name: business.name,
                department_name: taskItem.department_name,
                assignee: taskItem.assignee,
                sub_assignee: taskItem.sub_assignee,
                manager: taskItem.manager,
                reviewer: taskItem.reviewer,
                orgHierarchy: itemPath,
                originalData: taskItem
              })
            })
          })
        })
      })
    })

    return flatData
  }, [data])

  // Filter data based on collapsed items and add isSelected flag
  const filteredTreeData = useMemo(() => {
    return treeData
      .filter(item => !isItemHidden(item.orgHierarchy, item.id))
      .filter(item => !visibleTypes || visibleTypes.includes(item.type))
      .map(item => ({
        ...item,
        isSelected: item.id === selectedTaskId
      }))
  }, [treeData, isItemHidden, selectedTaskId, visibleTypes])

  // scrollToTaskId가 설정되면 해당 행으로 스크롤
  useEffect(() => {
    if (!scrollToTaskId || !gridApiRef.current) return

    const api = gridApiRef.current

    // treeData에서 해당 항목 찾기
    const targetItem = treeData.find(item => item.id === scrollToTaskId)
    if (!targetItem) {
      onScrollComplete?.()
      return
    }

    // 부모 항목들을 펼치기 위해 collapsedItems에서 제거
    const parentKeys: string[] = []
    for (let i = 1; i < targetItem.orgHierarchy.length; i++) {
      parentKeys.push(targetItem.orgHierarchy.slice(0, i).join('|'))
    }

    // 접힌 부모 항목이 있으면 펼치기
    const currentCollapsed = collapsedItems
    const hasCollapsedParent = parentKeys.some(key => currentCollapsed.has(key))

    if (hasCollapsedParent) {
      const newCollapsed = new Set(currentCollapsed)
      parentKeys.forEach(key => newCollapsed.delete(key))

      if (onCollapsedItemsChange) {
        onCollapsedItemsChange(newCollapsed)
      } else {
        setInternalCollapsedItems(newCollapsed)
      }

      // 다음 프레임에서 스크롤 실행 (펼침 애니메이션 후)
      setTimeout(() => {
        const rowNode = api.getRowNode(scrollToTaskId)
        if (rowNode) {
          api.ensureNodeVisible(rowNode, 'middle')
          // 하이라이트 효과를 위해 잠시 후 redraw
          setTimeout(() => {
            api.redrawRows({ rowNodes: [rowNode] })
          }, 100)
        }
        onScrollComplete?.()
      }, 100)
    } else {
      // 이미 펼쳐져 있으면 바로 스크롤
      const rowNode = api.getRowNode(scrollToTaskId)
      if (rowNode) {
        api.ensureNodeVisible(rowNode, 'middle')
        // 하이라이트 효과를 위해 잠시 후 redraw
        setTimeout(() => {
          api.redrawRows({ rowNodes: [rowNode] })
        }, 100)
      }
      onScrollComplete?.()
    }
  }, [scrollToTaskId, treeData, collapsedItems, onCollapsedItemsChange, onScrollComplete])

  // ===== Drag & Drop 핵심 함수 =====

  // 같은 부모 내 형제 항목들 가져오기
  const getSiblingsInParent = useCallback((
    item: TaskTreeNode,
    allItems: TaskTreeNode[]
  ): TaskTreeNode[] => {
    const parentId = getParentId(item)
    return allItems.filter(sibling =>
      sibling.type === item.type &&
      getParentId(sibling) === parentId
    )
  }, [getParentId])

  // 인접 항목 ID 계산
  const calculateAdjacentIds = useCallback((
    draggedItem: TaskTreeNode,
    targetItem: TaskTreeNode,
    dropPosition: 'before' | 'after'
  ): { prevItemId: string | number | null, nextItemId: string | number | null } => {
    const siblings = getSiblingsInParent(draggedItem, treeData)
      .filter(s => s.id !== draggedItem.id) // 드래그 중인 항목 제외

    const targetIndex = siblings.findIndex(s => s.id === targetItem.id)

    // task 유형의 경우 task_map_id 사용
    const getItemIdForApi = (item: TaskTreeNode): string | number | null => {
      if (item.type === 'task') {
        const taskMapId = (item.originalData as Task).task_map_id
        return taskMapId ? parseInt(taskMapId) : null
      }
      return item.id
    }

    if (dropPosition === 'before') {
      const prevItem = targetIndex > 0 ? siblings[targetIndex - 1] : null
      return {
        prevItemId: prevItem ? getItemIdForApi(prevItem) : null,
        nextItemId: getItemIdForApi(targetItem)
      }
    } else {
      const nextItem = targetIndex < siblings.length - 1 ? siblings[targetIndex + 1] : null
      return {
        prevItemId: getItemIdForApi(targetItem),
        nextItemId: nextItem ? getItemIdForApi(nextItem) : null
      }
    }
  }, [getSiblingsInParent, treeData])

  // API 호출 함수
  const callSortApi = useCallback(async (
    item: TaskTreeNode,
    prevId: string | number | null,
    nextId: string | number | null
  ): Promise<void> => {
    switch (item.type) {
      case 'task_group':
        await TaskGroupService.appsTaskViewsUpdateTaskGroupSort({
          groupId: item.id,
          requestBody: {
            prev_task_group_id: prevId as string | null,
            next_task_group_id: nextId as string | null
          }
        })
        break

      case 'task_subgroup':
        await TaskSubgroupService.appsTaskViewsUpdateTaskSubGroupSort({
          subGroupId: item.id,
          requestBody: {
            prev_task_sub_group_id: prevId as string | null,
            next_task_sub_group_id: nextId as string | null
          }
        })
        break

      case 'task':
        const taskMapId = (item.originalData as Task).task_map_id
        if (!taskMapId) {
          throw new Error('task_map_id not found for task')
        }
        await TaskMapService.appsTaskViewsUpdateTaskMapSort({
          mapId: parseInt(taskMapId),
          requestBody: {
            prev_task_map_id: prevId as number | null,
            next_task_map_id: nextId as number | null
          }
        })
        break

      case 'task_item':
        await TaskItemService.appsTaskViewsUpdateTaskItemSort({
          itemId: item.id,
          requestBody: {
            prev_task_item_id: prevId as string | null,
            next_task_item_id: nextId as string | null
          }
        })
        break

      default:
        throw new Error(`Unsupported type: ${item.type}`)
    }
  }, [])

  // 로컬 데이터 재정렬 함수
  const reorderLocalData = useCallback((
    draggedItem: TaskTreeNode,
    targetItem: TaskTreeNode,
    dropPosition: 'before' | 'after'
  ): Business[] => {
    const newData = JSON.parse(JSON.stringify(data)) as Business[]

    const type = draggedItem.type
    const parentId = getParentId(draggedItem)

    // 유형별 재정렬 로직
    if (type === 'task_group') {
      const business = newData.find(b => b.id === parentId)
      if (business) {
        const items = business.task_groups
        const draggedIndex = items.findIndex(i => i.id === draggedItem.id)
        const targetIndex = items.findIndex(i => i.id === targetItem.id)

        if (draggedIndex !== -1 && targetIndex !== -1) {
          const [removed] = items.splice(draggedIndex, 1)
          const newIndex = dropPosition === 'before'
            ? (draggedIndex < targetIndex ? targetIndex - 1 : targetIndex)
            : (draggedIndex < targetIndex ? targetIndex : targetIndex + 1)
          items.splice(newIndex, 0, removed)
        }
      }
    } else if (type === 'task_subgroup') {
      for (const business of newData) {
        const taskGroup = business.task_groups.find(tg => tg.id === parentId)
        if (taskGroup) {
          const items = taskGroup.task_subgroups
          const draggedIndex = items.findIndex(i => i.id === draggedItem.id)
          const targetIndex = items.findIndex(i => i.id === targetItem.id)

          if (draggedIndex !== -1 && targetIndex !== -1) {
            const [removed] = items.splice(draggedIndex, 1)
            const newIndex = dropPosition === 'before'
              ? (draggedIndex < targetIndex ? targetIndex - 1 : targetIndex)
              : (draggedIndex < targetIndex ? targetIndex : targetIndex + 1)
            items.splice(newIndex, 0, removed)
          }
          break
        }
      }
    } else if (type === 'task') {
      for (const business of newData) {
        for (const taskGroup of business.task_groups) {
          const taskSubgroup = taskGroup.task_subgroups.find(ts => ts.id === parentId)
          if (taskSubgroup) {
            const items = taskSubgroup.tasks
            const draggedIndex = items.findIndex(i => i.id === draggedItem.id)
            const targetIndex = items.findIndex(i => i.id === targetItem.id)

            if (draggedIndex !== -1 && targetIndex !== -1) {
              const [removed] = items.splice(draggedIndex, 1)
              const newIndex = dropPosition === 'before'
                ? (draggedIndex < targetIndex ? targetIndex - 1 : targetIndex)
                : (draggedIndex < targetIndex ? targetIndex : targetIndex + 1)
              items.splice(newIndex, 0, removed)
            }
            break
          }
        }
      }
    } else if (type === 'task_item') {
      for (const business of newData) {
        for (const taskGroup of business.task_groups) {
          for (const taskSubgroup of taskGroup.task_subgroups) {
            const task = taskSubgroup.tasks.find(t => t.id === parentId)
            if (task) {
              const items = task.task_items
              const draggedIndex = items.findIndex(i => i.id === draggedItem.id)
              const targetIndex = items.findIndex(i => i.id === targetItem.id)

              if (draggedIndex !== -1 && targetIndex !== -1) {
                const [removed] = items.splice(draggedIndex, 1)
                const newIndex = dropPosition === 'before'
                  ? (draggedIndex < targetIndex ? targetIndex - 1 : targetIndex)
                  : (draggedIndex < targetIndex ? targetIndex : targetIndex + 1)
                items.splice(newIndex, 0, removed)
              }
              break
            }
          }
        }
      }
    }

    return newData
  }, [data, getParentId])

  // ===== 드래그 이벤트 핸들러 =====

  // 드래그 클래스 적용 함수 (스크롤 시에도 호출됨)
  const applyDragClasses = useCallback((draggedItem: TaskTreeNode) => {
    const allRows = document.querySelectorAll('.ag-row[row-id]')
    allRows.forEach(rowEl => {
      const rowId = rowEl.getAttribute('row-id')
      if (!rowId) return

      const rowData = filteredTreeData.find(d => d.id === rowId)
      if (!rowData) return

      // 자기 자신은 row-dragging 클래스 추가
      if (rowData.id === draggedItem.id) {
        if (!rowEl.classList.contains('row-dragging')) {
          rowEl.classList.add('row-dragging')
        }
        return
      }

      // 유효한 드롭 대상인지 확인
      const isValidTarget = validateDropTarget(draggedItem, rowData)
      if (!isValidTarget) {
        if (!rowEl.classList.contains('drag-disabled')) {
          rowEl.classList.add('drag-disabled')
        }
      } else {
        // 유효한 대상이면 drag-disabled 제거
        rowEl.classList.remove('drag-disabled')
      }
    })
  }, [filteredTreeData, validateDropTarget])

  const handleDragStart = useCallback((item: TaskTreeNode) => {
    // 이전 드래그 상태 정리
    document.querySelectorAll('.ag-row.row-dragging, .ag-row.drop-target-before, .ag-row.drop-target-after, .ag-row.drag-disabled').forEach(el => {
      el.classList.remove('row-dragging', 'drop-target-before', 'drop-target-after', 'drag-disabled')
    })

    dragDataRef.current = item
    currentDropTargetRef.current = { targetId: null, position: null }
    const parentId = getParentId(item)

    // 드래그 불가능한 행들에 drag-disabled 클래스 추가
    applyDragClasses(item)

    setDragState({
      isDragging: true,
      draggedItem: item,
      draggedItemType: item.type,
      draggedItemParentId: parentId,
      dropTargetId: null,
      dropPosition: null
    })
  }, [getParentId, applyDragClasses])

  const handleDragEnd = useCallback(() => {
    dragDataRef.current = null
    currentDropTargetRef.current = { targetId: null, position: null }

    // 자동 스크롤 중지
    if (scrollIntervalRef.current) {
      cancelAnimationFrame(scrollIntervalRef.current)
      scrollIntervalRef.current = null
    }

    // 모든 드래그 관련 클래스 즉시 제거
    document.querySelectorAll('.ag-row.row-dragging, .ag-row.drop-target-before, .ag-row.drop-target-after, .ag-row.drag-disabled').forEach(el => {
      el.classList.remove('row-dragging', 'drop-target-before', 'drop-target-after', 'drag-disabled')
    })

    setDragState({
      isDragging: false,
      draggedItem: null,
      draggedItemType: null,
      draggedItemParentId: null,
      dropTargetId: null,
      dropPosition: null
    })
  }, [])

  const handleDragOver = useCallback((
    e: React.DragEvent,
    targetItem: TaskTreeNode
  ) => {
    e.preventDefault()

    const draggedItem = dragDataRef.current
    if (!draggedItem) return

    const isValid = validateDropTarget(draggedItem, targetItem)

    if (!isValid) {
      e.dataTransfer.dropEffect = 'none'
      setDragState(prev => ({
        ...prev,
        dropTargetId: null,
        dropPosition: null
      }))
      return
    }

    e.dataTransfer.dropEffect = 'move'

    // 드롭 위치 계산
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const midY = rect.top + rect.height / 2
    const dropPosition = e.clientY < midY ? 'before' : 'after'

    setDragState(prev => ({
      ...prev,
      dropTargetId: targetItem.id,
      dropPosition
    }))
  }, [validateDropTarget])


  // 현재 드롭 대상 추적용 ref (즉각적인 업데이트를 위해)
  const currentDropTargetRef = useRef<{ targetId: string | null, position: 'before' | 'after' | null }>({
    targetId: null,
    position: null
  })

  // 드롭 대상 스타일 직접 조작 함수 (state 거치지 않고 즉시 반영)
  const updateDropTargetStyle = useCallback((targetId: string | null, position: 'before' | 'after' | null) => {
    // 이전 상태와 동일하면 스킵
    if (currentDropTargetRef.current.targetId === targetId &&
        currentDropTargetRef.current.position === position) {
      return
    }

    // 현재 상태 저장
    currentDropTargetRef.current = { targetId, position }

    // 모든 기존 드롭 대상 클래스 제거
    document.querySelectorAll('.ag-row.drop-target-before, .ag-row.drop-target-after').forEach(el => {
      el.classList.remove('drop-target-before', 'drop-target-after')
    })

    // 새 드롭 대상에 클래스 추가
    if (targetId && position) {
      const targetRow = document.querySelector(`[row-id="${targetId}"]`)
      if (targetRow) {
        targetRow.classList.add(`drop-target-${position}`)
      }
    }
  }, [])

  // Gripper 아이콘 렌더러
  const GripperRenderer = useCallback((params: ICellRendererParams<TaskTreeNode>) => {
    const item = params.data

    // business 타입은 gripper 숨김
    if (!item || item.type === 'business') {
      return null
    }

    return (
      <div
        className="gripper-handle"
        draggable
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move'
          e.dataTransfer.setData('text/plain', item.id)
          handleDragStart(item)
        }}
        onDragEnd={handleDragEnd}
      >
        <GripVertical
          className="w-4 h-4"
          style={{ color: designTokens.colors.gray[4] }}
        />
      </div>
    )
  }, [handleDragStart, handleDragEnd])

  // Type tag renderer
  const TypeTagRenderer = useCallback((params: ICellRendererParams<TaskTreeNode>) => {
    const type = params.data?.type
    const originalData = params.data?.originalData

    // Check if task is linked (is_origin === false)
    const isLinkedTask = type === 'task' &&
                        originalData &&
                        'is_origin' in originalData &&
                        originalData.is_origin === false

    const typeConfig = {
      business: { label: '● 사업', color: 'purple' as const },
      task_group: { label: '● 전략과제', color: 'error' as const },
      task_subgroup: { label: '● 실행과제', color: 'warning' as const },
      task: { label: isLinkedTask ? '● 연결세부실행과제' : '● 세부실행과제', color: 'success' as const },
      task_item: { label: '● 수행업무', color: 'info' as const }
    }

    const config = typeConfig[type as keyof typeof typeConfig]
    if (!config) return null

    return (
      <Tag color={config.color} size="small">
        {config.label}
      </Tag>
    )
  }, [])

  // Action button renderer
  const ActionButtonRenderer = useCallback((params: ICellRendererParams<TaskTreeNode>) => {
    const type = params.data?.type
    const id = params.data?.id
    const taskData = params.data
    
    const handleCellClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      // Set flag to prevent row click
      onSetActionButtonClicked?.(true)
      setTimeout(() => onSetActionButtonClicked?.(false), 100)
    }

    /**
     * Permission check for task item creation
     * - owner/admin: Always allowed
     * - normal: Only allowed if they are the assignee
     */
    const canCreateTaskItem = (): boolean => {
      if (!orgMember || !taskData || taskData.type !== 'task') {
        return false
      }

      const userRole = orgMember.role
      const userId = orgMember.id
      const taskAssigneeId = taskData.assignee?.id

      // Admin and Owner: Always allowed
      if (userRole === 'admin' || userRole === 'owner') {
        return true
      }

      // Normal user: Only allowed if they are the assignee
      if (userRole === 'normal') {
        return userId === taskAssigneeId
      }

      // Unknown role: Deny by default (fail-safe)
      return false
    }

    /**
     * Permission check for task group/subgroup/task creation
     * - owner/admin: Always allowed
     * - normal: Not allowed
     */
    const canCreateTaskHierarchy = (): boolean => {
      if (!orgMember) {
        return false
      }

      const userRole = orgMember.role

      // Admin and Owner: Always allowed
      if (userRole === 'admin' || userRole === 'owner') {
        return true
      }

      // Normal user: Not allowed
      return false
    }

    const buttonConfig = {
      business: {
        label: '전략과제 생성',
        onClick: (e: React.MouseEvent) => {
          handleCellClick(e)
          onCreateTaskGroup?.(id)
        },
        disabled: !canCreateTaskHierarchy()
      },
      task_group: {
        label: '실행과제 생성',
        onClick: (e: React.MouseEvent) => {
          handleCellClick(e)
          onCreateTaskSubgroup?.(id)
        },
        disabled: !canCreateTaskHierarchy()
      },
      task_subgroup: {
        label: '세부실행과제 추가',
        onClick: (e: React.MouseEvent) => {
          handleCellClick(e)
          onCreateTask?.(id)
        },
        disabled: !canCreateTaskHierarchy()
      },
      task: {
        label: '수행업무 생성',
        onClick: (e: React.MouseEvent) => {
          handleCellClick(e)
          onCreateTaskItem?.(id)
        },
        disabled: !canCreateTaskItem(),
        showReport: true
      },
      task_item: null
    }

    const config = buttonConfig[type as keyof typeof buttonConfig]
    if (!config) return null

    const handleReportClick = (e: React.MouseEvent) => {
      handleCellClick(e)
      onReportClick?.(params.data!)
    }

    return (
      <div className="flex items-center gap-2 h-full">
        <Button
          size="small"
          color="white"
          onClick={config.onClick}
          disabled={config.disabled}
          className="flex items-center gap-1"
        >
          <Plus className="w-4 h-4" />
          {config.label}
        </Button>
        {config.showReport && (
          <Button
            size="small"
            color="white"
            onClick={handleReportClick}
            className="flex items-center gap-1"
          >
            <FileText className="w-3 h-3" />
            보고서
          </Button>
        )}
      </div>
    )
  }, [orgMember, onSetActionButtonClicked, onCreateTaskGroup, onCreateTaskSubgroup, onCreateTask, onCreateTaskItem, onReportClick])

  // Custom name renderer with hierarchy indentation and expand/collapse icons
  const NameRenderer = useCallback((params: ICellRendererParams<TaskTreeNode>) => {
    const data = params.data
    if (!data) return null

    // Calculate indentation based on hierarchy depth
    const depth = data.orgHierarchy.length - 1
    const indent = depth * 24 // 24px per level
    const itemPath = data.orgHierarchy.join('|')
    const isCollapsed = collapsedItems.has(itemPath)
    const itemHasChildren = hasChildren(data, treeData)

    // Check if task is linked (is_linked === true OR is_performance_linked === true)
    const isLinkedTask = data.type === 'task' &&
                        data.originalData &&
                        (
                          ('is_linked' in data.originalData && data.originalData.is_linked === true) ||
                          ('is_performance_linked' in data.originalData && data.originalData.is_performance_linked === true)
                        )

    return (
      <div
        className="flex items-center w-full h-full"
        style={{ paddingLeft: `${indent}px` }}
      >
        {/* Icon - Expand/Collapse for parent items, Circle for task_item */}
        <div
          className={`flex items-center justify-center w-5 h-5 mr-2 rounded ${
            data.type === 'task_item' ? '' : 'cursor-pointer hover:bg-gray-100'
          }`}
          onClick={(e) => {
            e.stopPropagation()
            // Set flag to prevent row click
            onSetActionButtonClicked?.(true)
            setTimeout(() => onSetActionButtonClicked?.(false), 100)
            if (data.type !== 'task_item' && itemHasChildren) {
              toggleCollapse(itemPath)
            }
          }}
          style={{
            visibility: data.type === 'task_item' || itemHasChildren ? 'visible' : 'hidden',
            opacity: data.type === 'task_item' || itemHasChildren ? 1 : 0
          }}
        >
          {data.type === 'task_item' ? (
            // Circle icon for task_item (수행업무)
            <Circle
              className="w-3 h-3"
              style={{
                color: designTokens.colors.gray[4],
                fill: designTokens.colors.gray[4]
              }}
            />
          ) : (
            // Expand/Collapse icons for parent items
            itemHasChildren && (
              isCollapsed ? (
                <ChevronDown
                  className="w-4 h-4"
                  style={{ color: designTokens.colors.gray[4] }}
                />
              ) : (
                <ChevronUp
                  className="w-4 h-4"
                  style={{ color: designTokens.colors.gray[4] }}
                />
              )
            )
          )}
        </div>

        {/* Link Icon for linked tasks */}
        {isLinkedTask && (
          <Link
            className="w-3.5 h-3.5 mr-1.5 flex-shrink-0"
            style={{ color: designTokens.colors.primary.DEFAULT }}
          />
        )}

        {/* Item Name */}
        <span style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: designTokens.typography.fontSize['body-medium'][0],
          color: designTokens.colors.gray[6],
          fontWeight: depth === 0 ? 600 : 400
        }}>
          {data.name}
        </span>
      </div>
    )
  }, [collapsedItems, hasChildren, treeData, onSetActionButtonClicked, toggleCollapse])

  // Column definitions
  const columnDefs: ColDef[] = useMemo(() => [
    // Gripper Column (순서 변경)
    {
      headerName: '',
      field: 'gripper',
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      pinned: 'left',
      sortable: false,
      filter: false,
      cellRenderer: GripperRenderer,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0
      }
    },
    {
      headerName: '명칭',
      field: 'name',
      width: 600,
      minWidth: 600,
      maxWidth: 600,
      cellRenderer: NameRenderer,
      pinned: 'left',
      sortable: false,
      filter: false,
      cellStyle: {
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: designTokens.typography.fontSize['body-medium'][0],
        color: designTokens.colors.gray[6]
      }
    },
    {
      headerName: '액션',
      field: 'action',
      width: 270,
      cellRenderer: ActionButtonRenderer,
      sortable: false,
      filter: false,
      pinned: 'left'
    },
    {
      headerName: '유형',
      field: 'type',
      width: 150,
      minWidth: 150,
      maxWidth: 150,
      cellRenderer: TypeTagRenderer,
      sortable: true,
      filter: 'agTextColumnFilter',
      valueGetter: (params) => {
        const type = params.data?.type
        const originalData = params.data?.originalData

        // Check if task is linked (is_origin === false)
        const isLinkedTask = type === 'task' &&
                            originalData &&
                            'is_origin' in originalData &&
                            originalData.is_origin === false

        const typeConfig: { [key: string]: string } = {
          business: '사업',
          task_group: '전략과제',
          task_subgroup: '실행과제',
          task: isLinkedTask ? '연결세부실행과제' : '세부실행과제',
          task_item: '수행업무'
        }

        return typeConfig[type as string] || type
      },
      filterParams: {
        filterOptions: ['equals'],
        defaultOption: 'equals',
        suppressAndOrCondition: true
      }
    },
    {
      headerName: '수행업무상태',
      field: 'task_item_status',
      width: 130,
      minWidth: 130,
      maxWidth: 130,
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'equals'],
        defaultOption: 'contains',
        suppressAndOrCondition: true
      },
      suppressSizeToFit: true,
      valueGetter: (params) => {
        const type = params.data?.type
        const originalData = params.data?.originalData

        if (type === 'task_item') {
          // 수행업무인 경우 표시 라벨 반환
          const taskItem = originalData as TaskItem
          const status = taskItem?.status
          switch (status) {
            case 'completed': return '완료 (관리자)'
            case 'reviewed': return '완료 (검토자)'
            case 'assigned': return '미완료 (관리자)'
            case 'incomplete': return '미완료 (담당자)'
            default: return '-'
          }
        } else if (type === 'task') {
          // 세부실행과제인 경우 완료/전체 계산
          const task = originalData as Task
          const taskItems = task?.task_items || []
          const totalCount = taskItems.length
          const completedCount = taskItems.filter(
            item => item.status === 'completed' || item.status === 'reviewed'
          ).length
          return totalCount > 0 ? `${completedCount}/${totalCount}` : '-'
        }
        return '-'
      },
      cellRenderer: (params: any) => {
        const type = params.data?.type
        const originalData = params.data?.originalData

        if (type === 'task_item') {
          const taskItem = originalData as TaskItem
          const status = taskItem?.status
          if (!status) return '-'

          const getStatusInfo = (status: string) => {
            switch (status) {
              case 'completed':
                return { color: 'success', label: '완료 (관리자)' }
              case 'reviewed':
                return { color: 'info', label: '완료 (검토자)' }
              case 'assigned':
                return { color: 'warning', label: '미완료 (관리자)' }
              case 'incomplete':
              default:
                return { color: 'error', label: '미완료 (담당자)' }
            }
          }

          const statusInfo = getStatusInfo(status)
          return React.createElement(Tag, {
            size: 'small',
            color: statusInfo.color
          }, statusInfo.label)
        } else if (type === 'task') {
          const task = originalData as Task
          const taskItems = task?.task_items || []
          const totalCount = taskItems.length
          const completedCount = taskItems.filter(
            item => item.status === 'completed' || item.status === 'reviewed'
          ).length

          if (totalCount === 0) return '-'

          const isAllCompleted = completedCount === totalCount
          return React.createElement(
            'span',
            {
              style: {
                color: isAllCompleted
                  ? designTokens.colors.success.DEFAULT
                  : designTokens.colors.gray[5],
                fontWeight: isAllCompleted ? 600 : 400
              }
            },
            `${completedCount}/${totalCount}`
          )
        }
        return '-'
      },
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      } as any
    },
    {
      headerName: '세부실행과제상태',
      field: 'task_status',
      width: 150,
      minWidth: 150,
      maxWidth: 150,
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'equals'],
        defaultOption: 'contains',
        suppressAndOrCondition: true
      },
      suppressSizeToFit: true,
      valueGetter: (params) => {
        const type = params.data?.type
        if (type !== 'task') return '-'

        const originalData = params.data?.originalData as Task
        const status = originalData?.status
        switch (status) {
          case 'completed': return '완료'
          case 'incomplete': return '미완료'
          default: return '-'
        }
      },
      cellRenderer: (params: any) => {
        const type = params.data?.type
        if (type !== 'task') return '-'

        const originalData = params.data?.originalData as Task
        const status = originalData?.status
        if (!status) return '-'

        const getStatusInfo = (status: string) => {
          switch (status) {
            case 'completed':
              return { color: 'success', label: '완료' }
            case 'incomplete':
            default:
              return { color: 'error', label: '미완료' }
          }
        }

        const statusInfo = getStatusInfo(status)
        return React.createElement(Tag, {
          size: 'small',
          color: statusInfo.color
        }, statusInfo.label)
      },
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      } as any
    },
    {
      headerName: '성과상태',
      field: 'task_performance_status',
      width: 150,
      minWidth: 150,
      maxWidth: 150,
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'equals'],
        defaultOption: 'contains',
        suppressAndOrCondition: true
      },
      suppressSizeToFit: true,
      valueGetter: (params) => {
        const type = params.data?.type
        if (type !== 'task') return '-'

        const originalData = params.data?.originalData as Task
        const status = originalData?.performance?.status
        switch (status) {
          case 'completed': return '완료'
          case 'incomplete': return '미완료'
          default: return '-'
        }
      },
      cellRenderer: (params: any) => {
        const type = params.data?.type
        if (type !== 'task') return '-'

        const originalData = params.data?.originalData as Task
        const status = originalData?.performance?.status
        if (!status) return '-'

        const getStatusInfo = (status: string) => {
          switch (status) {
            case 'completed':
              return { color: 'success', label: '완료' }
            case 'incomplete':
            default:
              return { color: 'error', label: '미완료' }
          }
        }

        const statusInfo = getStatusInfo(status)
        return React.createElement(Tag, {
          size: 'small',
          color: statusInfo.color
        }, statusInfo.label)
      },
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      } as any
    },
    {
      headerName: '부서',
      field: 'department_name',
      flex: 1,
      maxWidth: 200,
      cellStyle: {
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: designTokens.typography.fontSize['body-medium'][0],
        color: designTokens.colors.gray[6]
      },
      valueGetter: (params) => params.data?.department_name || '-',
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'equals', 'startsWith', 'endsWith'],
        defaultOption: 'contains',
        suppressAndOrCondition: true
      }
    },
    {
      headerName: '담당자',
      field: 'assignee',
      width: 100,
      minWidth: 100,
      maxWidth: 100,
      sortable: true,
      filter: 'agTextColumnFilter',
      valueGetter: (params) => params.data?.assignee?.name || '',
      filterParams: {
        filterOptions: ['contains', 'equals', 'startsWith', 'endsWith'],
        defaultOption: 'contains',
        suppressAndOrCondition: true
      }
    },
    {
      headerName: '관리자',
      field: 'manager',
      width: 100,
      minWidth: 100,
      maxWidth: 100,
      sortable: true,
      filter: 'agTextColumnFilter',
      valueGetter: (params) => params.data?.manager?.name || '',
      filterParams: {
        filterOptions: ['contains', 'equals', 'startsWith', 'endsWith'],
        defaultOption: 'contains',
        suppressAndOrCondition: true
      }
    },
    {
      headerName: '검토자',
      field: 'reviewer',
      width: 100,
      minWidth: 100,
      maxWidth: 100,
      sortable: true,
      filter: 'agTextColumnFilter',
      valueGetter: (params) => params.data?.reviewer?.name || '',
      filterParams: {
        filterOptions: ['contains', 'equals', 'startsWith', 'endsWith'],
        defaultOption: 'contains',
        suppressAndOrCondition: true
      }
    },
    {
      headerName: '라벨',
      field: 'labels',
      width: 200,
      minWidth: 120,
      sortable: false,
      filter: false,
      valueGetter: (params) => {
        const type = params.data?.type
        if (type !== 'task') return ''

        const originalData = params.data?.originalData as Task
        const labels = originalData?.labels
        if (!labels || labels.length === 0) return ''

        return labels.map(l => l.key).join(', ')
      },
      cellRenderer: (params: ICellRendererParams<TaskTreeNode>) => {
        const type = params.data?.type
        if (type !== 'task') return null

        const originalData = params.data?.originalData as Task
        const labels = originalData?.labels
        if (!labels || labels.length === 0) return null

        return React.createElement(
          'div',
          { className: 'flex items-center gap-1 flex-wrap' },
          labels.map((label, index) =>
            React.createElement(Tag, {
              key: label.id || index,
              size: 'small',
              color: 'gray'
            }, label.key)
          )
        )
      },
      cellStyle: {
        display: 'flex',
        alignItems: 'center'
      } as any
    }
  ], [GripperRenderer, NameRenderer, TypeTagRenderer, ActionButtonRenderer])

  // 컬럼 선택에서 끈 컬럼은 숨긴다.
  // 하나라도 숨기면 명칭이 빈 폭을 모두 채운다. 고정(pinned) 컬럼은 늘어나지 않으므로 이때는 고정을 풀고,
  // 액션·부서 등 나머지 컬럼은 정해진 폭을 유지한다.
  const displayColumnDefs: ColDef[] = useMemo(() => {
    // flex·pinned·폭은 다시 명시해야 원래대로 돌아온다 (새 정의에 빠진 값은 그리드가 이전 값을 유지)
    const cols: ColDef[] = columnDefs.map(col => ({
      ...col,
      flex: col.flex ?? null,
      hide: !!col.field && !!hiddenColumns?.includes(col.field)
    }))
    if (!cols.some(col => col.hide)) return cols
    return cols.map(col => {
      const unpinned: ColDef = { ...col, pinned: null }
      if (col.field === 'name') return { ...unpinned, flex: 1, minWidth: 600, maxWidth: 100000 }
      if (col.flex) return { ...unpinned, flex: null, width: col.maxWidth ?? 200 }
      return unpinned
    })
  }, [columnDefs, hiddenColumns])

  const defaultColDef = useMemo(() => ({
    headerHeight: 48,
    rowHeight: 60,
    headerClass: 'custom-header',
    cellClass: 'custom-cell'
  }), [])

  // 상태 값을 한글로 변환하는 함수
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return '완료 (관리자)'
      case 'reviewed':
        return '완료 (검토자)'
      case 'assigned':
        return '미완료 (관리자)'
      case 'incomplete':
      default:
        return '미완료 (담당자)'
    }
  }

  // 유형 값을 한글로 변환하는 함수
  const getTypeLabel = (type: string, originalData?: any) => {
    const isLinkedTask = type === 'task' &&
                        originalData &&
                        'is_origin' in originalData &&
                        originalData.is_origin === false

    const typeConfig: { [key: string]: string } = {
      business: '사업',
      task_group: '전략과제',
      task_subgroup: '실행과제',
      task: isLinkedTask ? '연결세부실행과제' : '세부실행과제',
      task_item: '수행업무'
    }

    return typeConfig[type] || type
  }

  // 그리드 컨테이너에서 row-id로 데이터 찾기
  const findRowDataFromElement = useCallback((element: HTMLElement): TaskTreeNode | null => {
    // 가장 가까운 .ag-row 요소 찾기
    const rowElement = element.closest('.ag-row') as HTMLElement
    if (!rowElement) return null

    // row-id 속성에서 ID 추출
    const rowId = rowElement.getAttribute('row-id')
    if (!rowId) return null

    // filteredTreeData에서 해당 ID의 데이터 찾기
    return filteredTreeData.find(item => item.id === rowId) || null
  }, [filteredTreeData])

  // 자동 스크롤 처리 함수 (드래그 중 가장자리 영역에서 자동 스크롤)
  // NOTE: HTML5 Drag & Drop API 제한으로 휠 스크롤 불가, 자동 스크롤이 유일한 방법
  const handleAutoScroll = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    const gridContainer = e.currentTarget
    const viewport = gridContainer.querySelector('.ag-body-viewport') as HTMLElement
    if (!viewport) return

    const viewportRect = viewport.getBoundingClientRect()
    const mouseY = e.clientY

    // 스크롤 영역 설정 (상하 120px 영역에서 스크롤 트리거 - 확대)
    const scrollZone = 120
    // 기본 스크롤 속도 증가 (더 빠른 반응)
    const baseScrollSpeed = 15

    // 이전 스크롤 인터벌 정리
    if (scrollIntervalRef.current) {
      cancelAnimationFrame(scrollIntervalRef.current)
      scrollIntervalRef.current = null
    }

    // 상단 스크롤 영역
    if (mouseY < viewportRect.top + scrollZone && mouseY > viewportRect.top) {
      // 가장자리에 가까울수록 빠르게 (intensity: 0~1)
      const intensity = 1 - (mouseY - viewportRect.top) / scrollZone
      // 최소 속도 보장 + intensity 기반 가속
      const scrollSpeed = baseScrollSpeed * (0.3 + intensity * 0.7)
      const scroll = () => {
        viewport.scrollTop -= scrollSpeed
        const draggedItem = dragDataRef.current
        if (draggedItem) {
          // 스크롤 직후 즉시 클래스 재적용 (지연 없이)
          applyDragClasses(draggedItem)
          scrollIntervalRef.current = requestAnimationFrame(scroll)
        }
      }
      scrollIntervalRef.current = requestAnimationFrame(scroll)
    }
    // 하단 스크롤 영역
    else if (mouseY > viewportRect.bottom - scrollZone && mouseY < viewportRect.bottom) {
      const intensity = 1 - (viewportRect.bottom - mouseY) / scrollZone
      const scrollSpeed = baseScrollSpeed * (0.3 + intensity * 0.7)
      const scroll = () => {
        viewport.scrollTop += scrollSpeed
        const draggedItem = dragDataRef.current
        if (draggedItem) {
          // 스크롤 직후 즉시 클래스 재적용 (지연 없이)
          applyDragClasses(draggedItem)
          scrollIntervalRef.current = requestAnimationFrame(scroll)
        }
      }
      scrollIntervalRef.current = requestAnimationFrame(scroll)
    }
  }, [applyDragClasses])

  // 그리드 레벨 드래그 오버 핸들러
  const handleGridDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    const draggedItem = dragDataRef.current
    if (!draggedItem) return

    // 자동 스크롤 처리
    handleAutoScroll(e)

    // 새로 보이는 행들에 드래그 클래스 적용
    applyDragClasses(draggedItem)

    const targetData = findRowDataFromElement(e.target as HTMLElement)
    if (!targetData || targetData.type === 'business') {
      e.dataTransfer.dropEffect = 'none'
      updateDropTargetStyle(null, null)
      return
    }

    const isValid = validateDropTarget(draggedItem, targetData)
    if (!isValid) {
      e.dataTransfer.dropEffect = 'none'
      updateDropTargetStyle(null, null)
      setDragState(prev => ({
        ...prev,
        dropTargetId: null,
        dropPosition: null
      }))
      return
    }

    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'

    // 드롭 위치 계산
    const rowElement = (e.target as HTMLElement).closest('.ag-row') as HTMLElement
    if (rowElement) {
      const rect = rowElement.getBoundingClientRect()
      const midY = rect.top + rect.height / 2
      const dropPosition: 'before' | 'after' = e.clientY < midY ? 'before' : 'after'

      // 즉시 DOM 스타일 업데이트
      updateDropTargetStyle(targetData.id, dropPosition)

      // state도 업데이트 (드롭 처리용)
      setDragState(prev => ({
        ...prev,
        dropTargetId: targetData.id,
        dropPosition
      }))
    }
  }, [findRowDataFromElement, validateDropTarget, updateDropTargetStyle, handleAutoScroll, applyDragClasses])

  // 순서 변경 여부 확인 함수
  const isPositionChanged = useCallback((
    draggedItem: TaskTreeNode,
    targetItem: TaskTreeNode,
    dropPosition: 'before' | 'after'
  ): boolean => {
    const siblings = getSiblingsInParent(draggedItem, treeData)
    const draggedIndex = siblings.findIndex(s => s.id === draggedItem.id)
    const targetIndex = siblings.findIndex(s => s.id === targetItem.id)

    if (draggedIndex === -1 || targetIndex === -1) return true

    // "before"로 드롭: 드래그 항목 바로 다음 항목에 before로 드롭하면 변경 없음
    if (dropPosition === 'before' && targetIndex === draggedIndex + 1) {
      return false
    }

    // "after"로 드롭: 드래그 항목 바로 이전 항목에 after로 드롭하면 변경 없음
    if (dropPosition === 'after' && targetIndex === draggedIndex - 1) {
      return false
    }

    return true
  }, [getSiblingsInParent, treeData])

  // 그리드 레벨 드롭 핸들러
  const handleGridDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()

    const draggedItem = dragDataRef.current
    if (!draggedItem || !dragState.dropPosition || !dragState.dropTargetId) {
      handleDragEnd()
      return
    }

    const targetData = filteredTreeData.find(item => item.id === dragState.dropTargetId)
    if (!targetData) {
      handleDragEnd()
      return
    }

    // 유효성 재검사
    if (!validateDropTarget(draggedItem, targetData)) {
      handleDragEnd()
      return
    }

    // 순서 변경 여부 확인 - 변경이 없으면 API 호출하지 않음
    if (!isPositionChanged(draggedItem, targetData, dragState.dropPosition)) {
      handleDragEnd()
      return
    }

    // 이전 데이터 백업
    const previousData = JSON.parse(JSON.stringify(data))

    // 인접 ID 계산
    const { prevItemId, nextItemId } = calculateAdjacentIds(
      draggedItem,
      targetData,
      dragState.dropPosition
    )

    // 로컬 데이터 먼저 업데이트 (낙관적 업데이트)
    const newData = reorderLocalData(draggedItem, targetData, dragState.dropPosition)
    onDataUpdate?.(newData)

    try {
      // API 호출
      await callSortApi(draggedItem, prevItemId, nextItemId)
      showSuccess('순서가 변경되었습니다.')
    } catch (error) {
      // 실패 시 롤백
      onDataUpdate?.(previousData)
      showError('순서 변경에 실패했습니다.')
      console.error('Sort API error:', error)
    } finally {
      handleDragEnd()
    }
  }, [dragState.dropPosition, dragState.dropTargetId, filteredTreeData, data, validateDropTarget, isPositionChanged, calculateAdjacentIds, reorderLocalData, onDataUpdate, callSortApi, handleDragEnd, showSuccess, showError])

  // 그리드 레벨 드래그 리브 핸들러
  const handleGridDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    // 그리드 영역을 완전히 벗어났는지 확인
    const relatedTarget = e.relatedTarget as HTMLElement
    const gridContainer = e.currentTarget as HTMLElement

    if (!relatedTarget || !gridContainer.contains(relatedTarget)) {
      updateDropTargetStyle(null, null)
      setDragState(prev => ({
        ...prev,
        dropTargetId: null,
        dropPosition: null
      }))
    }
  }, [updateDropTargetStyle])

  // 스크롤 시 드래그 클래스 재적용
  useEffect(() => {
    const container = gridContainerRef.current
    if (!container) return

    const viewport = container.querySelector('.ag-body-viewport') as HTMLElement
    if (!viewport) return

    const handleScroll = () => {
      const draggedItem = dragDataRef.current
      if (draggedItem) {
        applyDragClasses(draggedItem)
      }
    }

    viewport.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      viewport.removeEventListener('scroll', handleScroll)
    }
  }, [applyDragClasses])

  if (treeData.length === 0 && !isLoading) {
    return (
      <div className="p-12 text-center bg-white rounded-lg border" style={{ borderColor: designTokens.colors.gray[1] }}>
        <div style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: designTokens.typography.fontSize['body-large'][0],
          color: designTokens.colors.gray[4]
        }}>
          표시할 업무가 없습니다.
        </div>
      </div>
    )
  }

  return (
    <div
      ref={gridContainerRef}
      className="w-full relative"
      onDragOver={handleGridDragOver}
      onDrop={handleGridDrop}
      onDragLeave={handleGridDragLeave}
    >
      {/* 자동 스크롤 영역 표시 (드래그 중에만 표시) */}
      {dragState.isDragging && (
        <>
          {/* 상단 스크롤 영역 (헤더 높이 40px 고려) */}
          <div
            className="absolute left-0 right-0 pointer-events-none z-10 flex items-center justify-center"
            style={{
              top: '40px',
              height: '120px',
              background: 'linear-gradient(to bottom, rgba(108, 172, 228, 0.25) 0%, rgba(108, 172, 228, 0.08) 70%, transparent 100%)',
              borderBottom: '2px dashed rgba(108, 172, 228, 0.6)'
            }}
          >
            <div
              className="px-4 py-2 rounded-full"
              style={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#2F4062',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
            >
              ↑ 위로 스크롤
            </div>
          </div>
          {/* 하단 스크롤 영역 */}
          <div
            className="absolute bottom-0 left-0 right-0 pointer-events-none z-10 flex items-center justify-center"
            style={{
              height: '120px',
              background: 'linear-gradient(to top, rgba(108, 172, 228, 0.25) 0%, rgba(108, 172, 228, 0.08) 70%, transparent 100%)',
              borderTop: '2px dashed rgba(108, 172, 228, 0.6)'
            }}
          >
            <div
              className="px-4 py-2 rounded-full"
              style={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#2F4062',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
            >
              ↓ 아래로 스크롤
            </div>
          </div>
        </>
      )}
      <DataGrid
        rowData={filteredTreeData}
        columnDefs={displayColumnDefs}
        defaultColDef={defaultColDef}
        animateRows={true}
        height="calc(100vh - 210px)"
        rowSelection={undefined}
        headerHeight={40}
        rowHeight={42}
        loading={isLoading}
        getRowId={(params) => params.data.id}
        getRowClass={getRowClass}
        onGridReady={(params) => {
          gridApiRef.current = params.api
        }}
        onRowClicked={(event) => {
          if (event.data && onRowClick) {
            onRowClick(event.data)
          }
        }}
        // 범위 선택 기능 활성화
        enableRangeSelection={true}
        rangeSelectionExcludeColumns={['action', 'gripper']}
        enableCellCopy={true}
        suppressRowClickSelection={true}
        rangeCopyFormatter={(colId, value) => {
          // 상태 포맷팅
          if (colId === 'status') {
            return value ? getStatusLabel(value) : '-'
          }
          // 유형 포맷팅 (valueGetter가 이미 처리하므로 그대로 반환)
          if (colId === 'type') {
            return value ? getTypeLabel(value) : '-'
          }
          // 담당자/관리자/검토자 (중첩 객체는 valueGetter가 처리)
          if (colId === 'assignee' || colId === 'manager' || colId === 'reviewer') {
            // value가 객체인 경우 name 추출, 아니면 그대로 사용
            if (value && typeof value === 'object' && 'name' in value) {
              return value.name || '-'
            }
            return value || '-'
          }
          // 기본값 처리
          if (value === null || value === undefined) return '-'
          return value.toString()
        }}
      />
    </div>
  )
}

export default TaskHierarchyTable